import { createContext, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Stack, CircularProgress } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { WorkflowState } from '@/types/workflows'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import { useGetWorkflowState } from '@/service-library/hooks/workflow-states'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import WorkflowStateDetails from './WorkflowStateDetails'

type WorkflowStatePageContextValue = ReturnType<typeof useGetWorkflowState> & {
  listQueryKey: QueryKey
  workflowState: WorkflowState
}

export const WorkflowStateContext =
  createContext<WorkflowStatePageContextValue>(
    {} as WorkflowStatePageContextValue,
  )

export const useWorkflowStatePageContext = () =>
  useContext(WorkflowStateContext)

type WorkflowStatePageProps = {
  workflowStateId: string
  workflowState?: WorkflowState
  listQueryKey: QueryKey
}

export default function WorkflowStatePage({
  workflowStateId,
  workflowState,
  listQueryKey,
}: WorkflowStatePageProps) {
  const navigate = useNavigate()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { appendCurrentParamsToUrl } = useLocationHelpers()

  const query = useGetWorkflowState({
    id: workflowStateId,
    filters: {
      fields__include: 'excluded_project_grid_fields_ids,workflow_state_type',
    },
    initialData: workflowState,
    staleTime: 1000,
  })

  const { workflowState: currentState, isPending } = query

  useEffect(() => {
    if (currentState && selectedWorkflow?.id !== currentState.workflow_id) {
      navigate(appendCurrentParamsToUrl('..'), { relative: 'path' })
    }
  })

  return (
    <>
      {currentState && (
        <ProjectBreadcrumb
          label={currentState?.name || ''}
          url={`../workflow-states/${currentState?.id}`}
        />
      )}

      {!isPending && !currentState && (
        <Alert severity="error">
          Unable to load this workflow state. You may not have access or this
          workflow state does not exist.
        </Alert>
      )}

      {isPending ? (
        <Stack alignItems="center" sx={{ p: 4 }}>
          <CircularProgress />
        </Stack>
      ) : (
        currentState && (
          <WorkflowStateContext.Provider
            value={{
              ...query,
              workflowState: currentState,
              listQueryKey,
            }}
          >
            <WorkflowStateDetails />
          </WorkflowStateContext.Provider>
        )
      )}
    </>
  )
}
